import { css, keyframes } from '@emotion/react';
import {
  useCallback,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  type FormEvent,
} from 'react';
import {
  Link,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import lottie, { AnimationItem } from 'lottie-web';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { apiStore } from '@/stores/ApiStore';
import { useSessionBootstrap } from '@/hooks/useSessionBootstrap';
import { useSessionSubscriptionCheckout } from '@/hooks/useSessionSubscriptionCheckout';
import { useWebTrialCheckout } from '@/hooks/useWebTrialCheckout';
import { usePolicyText } from '@/hooks/usePolicyText';
import { sendLoadSuccess, sendPaymentFailed } from '@/hooks/useWebViewMessaging';
import { PaywallHeader } from '@/components/paywall/PaywallHeader';
import { FeatureList, FeatureItem } from '@/components/paywall/FeatureList';
import { CtaButton } from '@/components/ui/CtaButton';
import { PaymentError } from '@/components/ui/PaymentError';
import { RestoreSubscription } from '@/components/paywall/RestoreSubscription';
import paywallAnim from '@/assets/animations/paywall_v2.json';
import { ROUTES } from '@/constants/routes';
import {
  getStoredAttParams,
  resolveAttributionEmail,
} from '@/utils/attParams';
import { isValidEmailAddress } from '@/utils/email';
import { syncPaywallMetaFromUrl } from '@/utils/paywallUrlParams';

const IOS_SYSTEM_BLUE = '#007aff';
const IOS_SYSTEM_BLUE_FOCUS = 'rgba(0, 122, 255, 0.35)';
const IOS_SYSTEM_BLUE_SOFT = 'rgba(0, 122, 255, 0.10)';

const ctaGradient = keyframes`
  0% {
    background-position: 25% 50%;
  }
  100% {
    background-position: 75% 50%;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const checkoutAttentionFirst = keyframes`
  0%, 100% { opacity: 0; }
  38% { opacity: 1; }
`;

const checkoutAttentionSecond = keyframes`
  0%, 100% { opacity: 0; }
  39% { opacity: 1; }
`;

const Screen = styled.div`
  max-width: 430px;
  margin: 0 auto;
  padding:
    var(--app-safe-area-top)
    calc(16px + var(--app-safe-area-right))
    calc(16px + var(--app-safe-area-bottom))
    calc(16px + var(--app-safe-area-left));
`;

const Hero = styled.section`
  padding-top: 24px;
  display: grid;
  gap: 12px;
`;

const Features = styled.div`
  margin-top: 21px;
  margin-bottom: 36px;
`;

const Art = styled.div`
  display: grid;
  justify-content: center;
  align-items: center;
`;

const Disclaimer = styled.p`
  text-align: center;
  font-weight: 500;
  font-size: 15px;
  line-height: 122%;
  letter-spacing: 0.41px;
  text-align: center;
  margin-top: 25px;
  margin-bottom: 12px;
`;

const Underline = styled.span`
  color: #37b4eb;
`;

const CtaSection = styled.section`
  margin-bottom: 8px;
`;

const CheckoutSection = styled.section<{ $attentionSequence: number }>`
  position: relative;
  margin: 22px 0 16px;
  padding: 16px;
  border: 1px solid #d7d7dd;
  border-radius: 18px;
  background: #f5f5f7;
  box-shadow: 0 12px 28px rgba(21, 21, 42, 0.08);

  &::after {
    position: absolute;
    z-index: 3;
    inset: -5px;
    border: 3px solid rgba(0, 122, 255, 0.62);
    border-radius: 23px;
    content: '';
    opacity: 0;
    pointer-events: none;
    animation: ${({ $attentionSequence }) => {
      if ($attentionSequence === 0) {
        return 'none';
      }

      return $attentionSequence % 2 === 0
        ? css`${checkoutAttentionSecond} 620ms ease-out both`
        : css`${checkoutAttentionFirst} 620ms ease-out both`;
    }};
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

const PaymentForm = styled.form``;

const ApplePayArea = styled.div`
  width: 100%;
  height: 44px;
  margin-bottom: 18px;
`;

const ApplePayButton = styled.button`
  display: block;
  width: 100%;
  height: 44px;
  min-height: 44px;
  border: 1px solid #000000;
  border-radius: 9px;
  background: #ffffff;
  cursor: pointer;
  -webkit-appearance: -apple-pay-button;
  -apple-pay-button-type: plain;
  -apple-pay-button-style: white-outline;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: -4px;
  }
`;

const Divider = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;
  align-items: center;
  margin: 0 0 16px;
  color: #9999a2;
  font-size: 12px;
  text-transform: uppercase;

  &::before,
  &::after {
    content: '';
    height: 1px;
    background: #d9d9de;
  }
`;

const FieldGroup = styled.div`
  margin-bottom: 14px;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 7px;
  color: #34343f;
  font-size: 14px;
  line-height: 1.2;
  font-weight: 650;
`;

const EmailLabel = styled(FieldLabel)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 5px;
`;

const RequiredBadge = styled.span`
  display: inline-flex;
  min-height: 20px;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  background: ${IOS_SYSTEM_BLUE_SOFT};
  color: ${IOS_SYSTEM_BLUE};
  font-size: 10px;
  line-height: 1;
  font-weight: 750;
  letter-spacing: 0.025em;
  text-transform: uppercase;
`;

const EmailHint = styled.p`
  margin: 0 0 8px;
  color: #777783;
  font-size: 12px;
  line-height: 1.35;
`;

const EmailInput = styled.input<{ $invalid: boolean }>`
  width: 100%;
  min-height: 50px;
  padding: 12px 14px;
  border: 1px solid ${({ $invalid }) => ($invalid ? '#e5484d' : '#c9c9d0')};
  border-radius: 12px;
  background: ${({ $invalid }) => ($invalid ? '#fffafa' : '#ffffff')};
  color: #17172d;
  font: inherit;
  font-size: 16px;
  line-height: 1.2;
  outline: 0;
  transition: border-color 160ms ease, background 160ms ease, box-shadow 160ms ease;

  &::placeholder {
    color: #9b9ba4;
  }

  &:focus-visible {
    border-color: ${({ $invalid }) => ($invalid ? '#e5484d' : IOS_SYSTEM_BLUE)};
    box-shadow: 0 0 0 3px ${({ $invalid }) => (
      $invalid ? 'rgba(229, 72, 77, 0.15)' : 'rgba(0, 122, 255, 0.18)'
    )};
  }
`;

const EmailError = styled.div`
  margin-top: 8px;
  color: #a92f36;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 550;
`;

const StripeField = styled.div`
  position: relative;
  min-height: 176px;
`;

const StripeLoading = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  background: rgba(245, 245, 247, 0.86);
`;

const Spinner = styled.span`
  width: 28px;
  height: 28px;
  border: 3px solid rgba(0, 122, 255, 0.18);
  border-top-color: ${IOS_SYSTEM_BLUE};
  border-radius: 50%;
  animation: ${spin} 760ms linear infinite;
`;

const SubmitButton = styled.button`
  width: 100%;
  min-height: 52px;
  border: 0;
  border-radius: 13px;
  background: linear-gradient(90deg, #54c7fa, #29b9f8);
  background-size: 260% 100%;
  animation: ${ctaGradient} 4.2s ease-in-out infinite alternate;
  color: #ffffff;
  font: inherit;
  font-size: 16px;
  line-height: 1.2;
  font-weight: 750;
  cursor: pointer;

  &:disabled {
    animation: none;
    cursor: not-allowed;
    opacity: 0.48;
    filter: grayscale(20%);
  }

  &:focus-visible {
    outline: 3px solid ${IOS_SYSTEM_BLUE_FOCUS};
    outline-offset: 3px;
  }
`;

const RetryButton = styled.button`
  display: block;
  min-height: 44px;
  margin: 10px auto 0;
  padding: 8px 14px;
  border: 0;
  background: transparent;
  color: ${IOS_SYSTEM_BLUE};
  font: inherit;
  font-size: 14px;
  font-weight: 650;
  cursor: pointer;
`;

const PaymentState = styled.div`
  display: grid;
  min-height: 120px;
  place-items: center;
  align-content: center;
`;

const LegalDisclosure = styled.section`
  padding-top: 12px;
  color: #141414;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0.3px;
  text-align: center;

  p {
    margin: 0 0 16px;
  }

  a {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const features: FeatureItem[] = [
  { icon: '🧹', text: 'Clear Stored Cache and Temporary Data' },
  { icon: '🔒', text: 'Protect Personal Data While Cleaning' },
  { icon: '📦', text: 'Free Up Storage for Better Performance' },
];

const openedCheckoutLocationKeys = new Set<string>();

export const PaywallV2 = observer(() => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isPaymentReturn = searchParams.has('payment_intent_client_secret');
  const checkoutWasOpened = openedCheckoutLocationKeys.has(location.key);
  const lottieElRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [checkoutVisible, setCheckoutVisible] = useState(
    isPaymentReturn || checkoutWasOpened,
  );
  const [checkoutAttentionSequence, setCheckoutAttentionSequence] = useState(0);
  const [paymentElementContainer, setPaymentElementContainer] = useState<HTMLDivElement | null>(null);
  const ctaSectionRef = useRef<HTMLElement | null>(null);
  const checkoutAttentionFrameRef = useRef<number | null>(null);
  const checkoutScrollFrameRef = useRef<number | null>(null);
  const shouldRestoreCheckoutAnchorRef = useRef(checkoutWasOpened);
  const checkoutReadyScrollBehaviorRef = useRef<ScrollBehavior | null>(
    checkoutWasOpened ? 'auto' : null,
  );
  const initialAttEmail = resolveAttributionEmail(
    { att_email: searchParams.get('att_email') ?? '' },
    getStoredAttParams()?.att_email,
  ) ?? '';
  const [email, setEmail] = useState(initialAttEmail);
  const [emailTouched, setEmailTouched] = useState(false);
  const loadStatusReportedRef = useRef(false);
  const sessionBootstrap = useSessionBootstrap();
  const {
    activate: activateSessionSubscription,
    available: sessionCheckoutAvailable,
    error: sessionActivationError,
    processing: sessionProcessing,
  } = useSessionSubscriptionCheckout('paywall');
  const isSessionCheckout = sessionCheckoutAvailable
    && apiStore.user?.checkoutMode === 'session';

  const {
    trialDays,
    billingFrequencyLabel,
    recurringPriceFormatted,
    trialPriceFormatted,
  } = usePolicyText();

  const weeklyTrialPlan = subscriptionStore.trialPlans.find(
    (plan) => plan.billingFrequency.toLowerCase() === 'weekly',
  ) ?? subscriptionStore.selectedTrialPlan;

  const {
    initializing,
    processing,
    cardComplete,
    cardReady,
    applePayAvailable,
    error,
    recovering,
    startApplePay,
    submitCard,
    retryInitialization,
    clearError,
  } = useWebTrialCheckout({
    enabled: (
      checkoutVisible
      && Boolean(weeklyTrialPlan)
      && sessionBootstrap.state === 'ready'
      && !isSessionCheckout
    ),
    plan: weeklyTrialPlan,
    paymentElementContainer,
  });

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!subscriptionStore.plans || subscriptionStore.plans.length === 0) {
        await subscriptionStore.fetchPlans();
      }

      if (cancelled) {
        return;
      }

      const weeklyPlan = subscriptionStore.trialPlans.find(
        (plan) => plan.billingFrequency.toLowerCase() === 'weekly',
      ) ?? subscriptionStore.selectedTrialPlan;

      if (weeklyPlan) {
        subscriptionStore.setSelectedTrialPlan(weeklyPlan.planId);
      }

      if (!loadStatusReportedRef.current) {
        loadStatusReportedRef.current = true;
        if (weeklyPlan) {
          sendLoadSuccess();
        }
        else {
          sendPaymentFailed('Subscription options are temporarily unavailable.');
        }
      }

      setIsPageLoading(false);
      syncPaywallMetaFromUrl(searchParams);
    };

    if (lottieElRef.current && !animRef.current) {
      animRef.current = lottie.loadAnimation({
        container: lottieElRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: paywallAnim,
      });
    }

    void init();

    return () => {
      cancelled = true;
      animRef.current?.destroy();
      animRef.current = null;
    };
  }, [searchParams]);

  useEffect(() => {
    if (isPaymentReturn) {
      setCheckoutVisible(true);
    }
  }, [isPaymentReturn]);

  useEffect(() => {
    return () => {
      if (checkoutAttentionFrameRef.current !== null) {
        window.cancelAnimationFrame(checkoutAttentionFrameRef.current);
      }
      if (checkoutScrollFrameRef.current !== null) {
        window.cancelAnimationFrame(checkoutScrollFrameRef.current);
      }
    };
  }, []);

  const scrollToCheckoutAnchor = useCallback((behavior: ScrollBehavior) => {
    if (checkoutScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(checkoutScrollFrameRef.current);
    }

    checkoutScrollFrameRef.current = window.requestAnimationFrame(() => {
      const ctaSection = ctaSectionRef.current;
      if (!ctaSection) {
        checkoutScrollFrameRef.current = null;
        return;
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const ctaBottom = window.scrollY + ctaSection.getBoundingClientRect().bottom;
      window.scrollTo({
        top: ctaBottom,
        left: 0,
        behavior: reduceMotion ? 'auto' : behavior,
      });
      checkoutScrollFrameRef.current = null;
    });
  }, []);

  useLayoutEffect(() => {
    if (
      !checkoutVisible
      || isPaymentReturn
      || !shouldRestoreCheckoutAnchorRef.current
    ) {
      return;
    }

    shouldRestoreCheckoutAnchorRef.current = false;
    scrollToCheckoutAnchor('auto');
  }, [checkoutVisible, isPaymentReturn, scrollToCheckoutAnchor]);

  useLayoutEffect(() => {
    const behavior = checkoutReadyScrollBehaviorRef.current;
    if (
      !checkoutVisible
      || isPaymentReturn
      || behavior === null
      || initializing
      || !cardReady
    ) {
      return;
    }

    checkoutReadyScrollBehaviorRef.current = null;
    scrollToCheckoutAnchor(behavior);
  }, [
    cardReady,
    checkoutVisible,
    initializing,
    isPaymentReturn,
    scrollToCheckoutAnchor,
  ]);

  const emailIsValid = isValidEmailAddress(email);
  const showEmailError = emailTouched && !emailIsValid;
  const emailErrorMessage = email.trim()
    ? 'Enter a valid email address, for example name@example.com.'
    : 'Enter your email address to continue.';

  const resetEmailValidation = () => {
    setEmailTouched(false);
    clearError();
  };

  const onApplePayClick = () => {
    resetEmailValidation();
    void startApplePay(email).catch(() => undefined);
  };

  const onRetryPaymentInitialization = () => {
    resetEmailValidation();
    retryInitialization();
  };

  const onCardSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!emailIsValid) {
      setEmailTouched(true);
      return;
    }

    void submitCard(email);
  };

  const triggerCheckoutAttention = () => {
    if (checkoutAttentionFrameRef.current !== null) {
      window.cancelAnimationFrame(checkoutAttentionFrameRef.current);
    }

    checkoutAttentionFrameRef.current = window.requestAnimationFrame(() => {
      setCheckoutAttentionSequence((current) => current + 1);
      checkoutAttentionFrameRef.current = null;
    });
  };

  const onCta = () => {
    if (sessionBootstrap.state !== 'ready' || sessionBootstrap.loading) {
      return;
    }

    if (isSessionCheckout) {
      const planId = weeklyTrialPlan?.planId;
      if (planId) {
        void activateSessionSubscription(planId);
      }
      return;
    }

    triggerCheckoutAttention();

    if (!checkoutVisible) {
      checkoutReadyScrollBehaviorRef.current = 'smooth';
      setCheckoutVisible(true);
      openedCheckoutLocationKeys.add(location.key);
    }

    scrollToCheckoutAnchor('smooth');
  };

  return (
    <Screen>
      <Hero>
        <PaywallHeader>Optimize & Protect Your Device</PaywallHeader>
        <Features>
          <FeatureList features={features} />
        </Features>
        <Art aria-hidden="true">
          <div ref={lottieElRef} style={{ width: '334px', height: '334px' }} />
        </Art>
      </Hero>

      <Disclaimer>
        Get full experience with
        {' '}
        <Underline>
          {trialDays}
          ‑day trial
        </Underline>
        {' '}
        for
        {' '}
        {trialPriceFormatted}
        ,
        <br />
        then
        {' '}
        {recurringPriceFormatted}
        {' '}
        per
        {' '}
        {billingFrequencyLabel}
        . Cancel anytime.
      </Disclaimer>

      <CtaSection ref={ctaSectionRef}>
        <CtaButton
          loading={isPageLoading || sessionBootstrap.loading || sessionProcessing}
          disabled={
            isPageLoading
            || sessionBootstrap.loading
            || sessionBootstrap.state === 'error'
            || sessionProcessing
            || !weeklyTrialPlan
          }
          onClick={onCta}
        >
          Start
          {' '}
          {trialDays}
          ‑Day Trial
        </CtaButton>
        <div role="alert" aria-live="assertive">
          <PaymentError
            error={sessionBootstrap.error || sessionActivationError}
          />
        </div>
        {sessionBootstrap.state === 'error' && (
          <RetryButton
            type="button"
            onClick={sessionBootstrap.retry}
          >
            Retry session
          </RetryButton>
        )}
      </CtaSection>

      {checkoutVisible
        && sessionBootstrap.state === 'ready'
        && !isSessionCheckout
        && (
          <CheckoutSection
            id="v2-checkout"
            aria-label="Payment options"
            $attentionSequence={checkoutAttentionSequence}
          >
            {isPaymentReturn
              ? (
                  <PaymentState role="status" aria-live="polite">
                    {recovering && <Spinner aria-hidden="true" />}
                    {!recovering && (
                      <div role="alert" aria-live="assertive">
                        <PaymentError error={error} />
                      </div>
                    )}
                  </PaymentState>
                )
              : !weeklyTrialPlan
                  ? (
                      <PaymentState>
                        {isPageLoading
                          ? <Spinner aria-hidden="true" />
                          : <PaymentError error="Subscription options are temporarily unavailable." />}
                      </PaymentState>
                    )
                  : (
                      <PaymentForm onSubmit={onCardSubmit} noValidate>
                        {applePayAvailable && (
                          <ApplePayArea>
                            <ApplePayButton
                              type="button"
                              aria-label="Pay with Apple Pay"
                              disabled={processing}
                              onClick={onApplePayClick}
                            />
                          </ApplePayArea>
                        )}
                        {applePayAvailable && <Divider>OR</Divider>}

                        <FieldGroup>
                          <EmailLabel htmlFor="v2-payment-email">
                            <span>Email address</span>
                            <RequiredBadge>Required</RequiredBadge>
                          </EmailLabel>
                          <EmailHint id="v2-payment-email-hint">
                            Used for your receipt and subscription access.
                          </EmailHint>
                          <EmailInput
                            id="v2-payment-email"
                            name="email"
                            type="email"
                            value={email}
                            placeholder="you@example.com"
                            autoComplete="email"
                            autoCapitalize="none"
                            autoCorrect="off"
                            enterKeyHint="next"
                            maxLength={254}
                            pattern="[^\\s@]+@[^\\s@]+\\.[^\\s@]+"
                            required
                            spellCheck={false}
                            disabled={processing}
                            $invalid={showEmailError}
                            aria-describedby={showEmailError
                              ? 'v2-payment-email-hint v2-payment-email-error'
                              : 'v2-payment-email-hint'}
                            aria-errormessage={showEmailError
                              ? 'v2-payment-email-error'
                              : undefined}
                            aria-invalid={showEmailError}
                            onBlur={() => setEmailTouched(true)}
                            onFocus={resetEmailValidation}
                            onChange={(event) => {
                              setEmail(event.target.value);
                              resetEmailValidation();
                            }}
                          />
                          {showEmailError && (
                            <EmailError
                              id="v2-payment-email-error"
                              role="alert"
                              aria-live="polite"
                            >
                              {emailErrorMessage}
                            </EmailError>
                          )}
                        </FieldGroup>

                        <FieldGroup>
                          <FieldLabel>Card details</FieldLabel>
                          <StripeField>
                            {(!cardReady || initializing) && (
                              <StripeLoading aria-label="Loading secure card form">
                                <Spinner aria-hidden="true" />
                              </StripeLoading>
                            )}
                            <div ref={setPaymentElementContainer} />
                          </StripeField>
                        </FieldGroup>

                        <SubmitButton
                          type="submit"
                          disabled={
                            processing
                            || initializing
                            || !cardReady
                            || !cardComplete
                            || !emailIsValid
                          }
                        >
                          {processing ? 'Processing…' : 'Continue'}
                        </SubmitButton>

                        <div role="alert" aria-live="assertive">
                          <PaymentError error={error} />
                        </div>
                        {error && !initializing && !cardReady && (
                          <RetryButton
                            type="button"
                            onClick={onRetryPaymentInitialization}
                          >
                            Reload payment form
                          </RetryButton>
                        )}
                      </PaymentForm>
                    )}
          </CheckoutSection>
        )}

      <LegalDisclosure aria-label="Subscription and billing terms">
        <p>
          Your card will be charged
          {' '}
          {trialPriceFormatted}
          {' '}
          today. After
          {' '}
          {trialDays}
          {' '}
          days, your subscription will automatically renew at
          {' '}
          {recurringPriceFormatted}
          {' '}
          every 7 days until canceled.
        </p>
        <p>
          Cancel anytime before your next billing date to avoid future charges. Payments already
          processed are non-refundable, except where required by applicable law or in case of a
          verified billing error.
        </p>
        <p>
          By continuing, you agree to our
          {' '}
          <Link to={ROUTES.POLICY.TERMS}>Terms of Use</Link>
          ,
          {' '}
          <Link to={ROUTES.POLICY.PRIVACY}>Privacy Policy</Link>
          {' '}
          and
          {' '}
          <Link to={ROUTES.POLICY.SUBSCRIPTION}>Refund Policy</Link>
          .
        </p>
      </LegalDisclosure>
      <RestoreSubscription />
    </Screen>
  );
});
